import type { UserManager } from 'oidc-client-ts';
import { REFRESH_MARGIN_SECONDS, type RefreshedToken } from '../shared/lib/apiClient';

export type TokenSource = Pick<UserManager, 'getUser' | 'signinSilent'>;

type StoredUser = { access_token: string; expires_at?: number; expires_in?: number } | null;

function usable(user: StoredUser, staleToken: string | null): RefreshedToken | null {
  if (!user?.access_token || user.access_token === staleToken) return null;
  if ((user.expires_in ?? 0) <= REFRESH_MARGIN_SECONDS) return null;
  return { token: user.access_token, expiresAt: user.expires_at };
}

/**
 * Renews the access token, cooperating with oidc-client-ts' own automaticSilentRenew:
 * on mobile resume both fire together, and a second refresh_token grant can be
 * rejected (invalid_grant) when the realm revokes refresh tokens on use.
 */
export async function refreshAccessToken(source: TokenSource, staleToken: string | null): Promise<RefreshedToken> {
  const alreadyRenewed = usable(await source.getUser(), staleToken);
  if (alreadyRenewed) return alreadyRenewed;

  try {
    const user = await source.signinSilent();
    if (!user?.access_token) throw new Error('silent renewal returned no user');
    return { token: user.access_token, expiresAt: user.expires_at };
  } catch (err) {
    const renewedConcurrently = usable(await source.getUser(), staleToken);
    if (renewedConcurrently) return renewedConcurrently;
    throw err;
  }
}
